import xLogo from "@/assets/x-logo.png.asset.json";
import bigdeltaMark from "@/assets/bigdelta-mark.png";
import { Linkedin, Github, Mail } from "lucide-react";

const footerLinks: Record<string, { label: string; href?: string; badge?: string }[]> = {
  Product: [
    { label: "Web analytics", href: "/web-analytics" },
    { label: "Session replays", href: "/session-replays" },
    { label: "Heatmaps", href: "/heatmaps" },
    { label: "Funnels", href: "/funnels" },
    { label: "Profiles", href: "/profiles" },
    { label: "Notifications", href: "/notifications" },
    { label: "Pricing", href: "/pricing" },
  ],
  Integrations: [
    { label: "Lovable", href: "/apps/lovable" },
    { label: "Replit", href: "/apps/replit" },
    { label: "V0", href: "/apps/v0" },
    { label: "Webflow", href: "/apps/webflow" },
    { label: "Wix", href: "/apps/wix" },
    { label: "Base44", href: "/apps/base44" },
    { label: "Wordpress", href: "/apps/wordpress" },
  ],
  "Use Cases": [
    { label: "Developers", href: "/use-cases/developers" },
    { label: "Marketers", href: "/use-cases/marketers" },
    { label: "E-commerce", href: "/use-cases/ecommerce" },
    { label: "SaaS", href: "/use-cases/saas" },
    { label: "Startups", href: "/use-cases/startups" },
  ],
  Resources: [
    { label: "Library", href: "/hub" },
    { label: "Metrics", href: "/metrics" },
    { label: "Guides", href: "/guides" },
    { label: "Calculators", href: "/calculators" },
    { label: "Glossary", href: "/glossary" },
    { label: "Docs", href: "http://docs.bigdelta.com" },
    { label: "Blog", href: "/blog" },
    { label: "Changelog", href: "https://docs.bigdelta.com/changelog" },
    { label: "Support", href: "/support" },
  ],
  Company: [
    { label: "About", href: "/about" },
    { label: "Privacy", href: "/privacy" },
    { label: "Data Deletion", href: "/data-deletion" },
    { label: "Terms", href: "/terms" },
  ],
};

const Footer = () => {
  return (
    <footer className="border-t border-border px-6 md:px-10 py-16">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col md:flex-row md:justify-between gap-12 mb-16">
          <div>
            <div className="mb-3 flex items-center gap-2">
              <img
                src={bigdeltaMark}
                alt="Bigdelta"
                width={20}
                height={20}
                className="h-5 w-5 rounded-md"
              />
              <span className="text-base font-semibold text-foreground">Bigdelta</span>
            </div>
          </div>

          <div className="flex gap-16">
            {Object.entries(footerLinks).map(([category, links]) => (
              <div key={category}>
                <p className="text-sm font-medium text-foreground mb-4">{category}</p>
                <ul className="space-y-2.5">
                  {links.map((link) => (
                    <li key={link.label}>
                      <a
                        href={link.href || "#"}
                        className="text-sm text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1.5"
                      >
                        {link.label}
                        {link.badge && (
                          <span className="inline-flex items-center justify-center w-4.5 h-4.5 text-[10px] font-medium rounded-full bg-muted-foreground/20 text-muted-foreground">
                            {link.badge}
                          </span>
                        )}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div className="flex flex-col-reverse md:flex-row md:items-center md:justify-between gap-4 pt-8 border-t border-border">
          <p className="text-sm text-muted-foreground">
            © {new Date().getFullYear()} Bigdelta. All rights reserved.
          </p>
          <div className="flex items-center gap-4">
            <a
              href="https://www.linkedin.com/company/bigdelta-com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-muted-foreground hover:text-foreground transition-colors"
              aria-label="LinkedIn"
            >
              <Linkedin size={18} />
            </a>
            <a
              href="https://x.com/bigdelta_com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-muted-foreground hover:text-foreground transition-colors"
              aria-label="X (formerly Twitter)"
            >
              <img
                src={xLogo.url}
                alt="X (formerly Twitter)"
                width={18}
                height={18}
                className="h-[18px] w-[18px] object-contain"
              />
            </a>
            <a
              href="https://github.com/bigdelta"
              target="_blank"
              rel="noopener noreferrer"
              className="text-muted-foreground hover:text-foreground transition-colors"
              aria-label="GitHub"
            >
              <Github size={18} />
            </a>
            <a
              href="mailto:info@bigdelta.com"
              className="text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Email"
            >
              <Mail size={18} />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
